import { sortBy, thru } from 'lodash'
import { useMemo } from 'react'

import { Gift } from '../../models/Gift'
import { Item } from '../../models/Item'
import { RecordFactory } from '../../models/RecordFactory'
import { Subaccount } from '../../models/Subaccount'
import { User } from '../../models/User'
import { currencyFormat } from '../../utils/numbers'
import { Icon } from '../Icon'
import { ClientSearchableSelect } from '../contacts/ClientSearchableSelect'
import { ContractSearchableSelect } from './ContractSearchableSelect'
import { TypeSearchableSelect } from './TypeSearchableSelect'
import { Chip } from '../common/fields/Chip'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { TextArea } from '../common/fields/TextArea'
import { ReceiptView } from '../workflows/ReceiptView'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    gift: Gift
    onUpdateGift: (updatedGift: Gift) => void
    owner: User
    giftItems: Item[]
}

export const NewGiftPage = ({ owner, gift, onUpdateGift, giftItems }: Props) => {
    const handleSelectClient = async (user: User | undefined) => {
        const client = await RecordFactory.getInstance().loadClient(user?.id ?? '')
        if (client !== undefined && (client.discount ?? 0) > 0) {
            const updatedGiftObj = gift.clone({
                client: user,
                discount_type: 'percent',
                discount: client.discount,
            })
            if (client.notify) {
                updatedGiftObj.channel = client.notify
            }
            onUpdateGift(updatedGiftObj)
        }
        else {
            const updatedGiftObj = gift.clone({ client: user })
            if (client?.notify) {
                updatedGiftObj.channel = client.notify
            }
            onUpdateGift(updatedGiftObj)
        }
    }

    const handleSelectSubaccount = (subaccount: Subaccount | undefined) => {
        const updatedGiftObj = gift.clone({ subaccount, client: subaccount?.client })
        onUpdateGift(updatedGiftObj)
    }

    const giftsSearchableItems = useMemo(() => {
        return sortBy(giftItems, (giftItem) => giftItem.name?.toLowerCase() ?? '').map(
            (giftItem) => ({
                id: giftItem.id ?? '',
                title: giftItem.name ?? '',
                subtitle:
                    giftItem.rate === undefined ? (
                        'TBD'
                    ) : (
                        <>
                            <span
                                className={(giftItem.discountAmount ?? 0) > 0 ? 'line-through' : ''}
                            >
                                {currencyFormat(giftItem.rate)}
                            </span>{' '}
                            {giftItem.rate !== undefined &&
                            giftItem.discountAmount !== undefined &&
                            giftItem.discountAmount > 0
                                ? currencyFormat(giftItem.rate - giftItem.discountAmount)
                                : ''}
                        </>
                    ),
                tertiaryView:
                    giftItem.image?.uri !== undefined ? (
                        <img src={giftItem.image.uri} className="size-9 rounded-full" />
                    ) : (
                        <Icon
                            icon="gift.large"
                            background="round"
                            className="size-9"
                            svgClassName="size-4"
                        />
                    ),
                giftItem,
            })
        )
    }, [giftItems])
    const selectedGift = gift.item
    const selectedGiftItem =
        selectedGift !== undefined
            ? thru(
                  giftsSearchableItems.find((gift) => gift.id === selectedGift.id) ?? null,
                  (giftItem) => {
                      if (giftItem === null) {
                          return null
                      }
                      const newGiftItem = {
                          ...giftItem,
                          tertiaryView:
                              giftItem.giftItem.image?.uri !== undefined ? (
                                  <img
                                      src={giftItem.giftItem.image.uri}
                                      className="size-6 rounded-full"
                                  />
                              ) : (
                                  <Icon icon="gift.large" background="round" className="size-4" />
                              ),
                      }
                      delete (newGiftItem as any).subtitle
                      return newGiftItem
                  }
              )
            : null
    const handleSelectNewGiftItem = (newGiftItem: Item | null) => {
        if (newGiftItem === null) {
            onUpdateGift(gift.clone({ item: undefined }))
            return
        }

        onUpdateGift(
            gift.clone({
                item: newGiftItem,
                expires: newGiftItem.expires,
                form: newGiftItem.form ?? gift.form,
                contract: newGiftItem.contract ?? gift.contract,
                location: newGiftItem.location,
                discount: newGiftItem.discount,
                discount_type: newGiftItem.discountType,
                total: newGiftItem.rate ?? 0,
            })
        )
    }

    return (
        <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
            <WorkflowPage.Card title="General">
                <WorkflowPage.IconSection title="Client (required)" icon="user.large">
                    <ClientSearchableSelect
                        mode="server"
                        selectedClient={gift.client}
                        selectedSubaccount={gift.subaccount}
                        onSelectClient={handleSelectClient}
                        onSelectSubaccount={handleSelectSubaccount}
                        shouldPreserveLastSelection={true}
                        canAddNew
                        shouldIncludeSubaccounts
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection title="Gift certificate (required)" icon="gift.large">
                    <SearchableSelect
                        items={giftsSearchableItems}
                        onSelectItem={(item) => handleSelectNewGiftItem(item?.giftItem ?? null)}
                        selectedItemOverride={selectedGiftItem}
                        titleWeight="bold"
                        placeholder="Type to search"
                        shouldPreserveLastSelection
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection title="Expires after" icon="clock.large">
                    <div className="flex flex-wrap items-center justify-start gap-x-2 gap-y-1">
                        <Chip
                            title="No expiration"
                            isSelected={gift.expires === undefined}
                            onClick={() => onUpdateGift(gift.clone({ expires: undefined }))}
                            size="small"
                        />
                        <Chip
                            title="1 year"
                            isSelected={gift.expires === 360}
                            onClick={() => onUpdateGift(gift.clone({ expires: 360 }))}
                            size="small"
                        />
                        <Chip
                            title="2 years"
                            isSelected={gift.expires === 720}
                            onClick={() => onUpdateGift(gift.clone({ expires: 720 }))}
                            size="small"
                        />
                        <Chip
                            title="5 years"
                            isSelected={gift.expires === 1800}
                            onClick={() => onUpdateGift(gift.clone({ expires: 1800 }))}
                            size="small"
                        />
                    </div>
                </WorkflowPage.IconSection>
            </WorkflowPage.Card>

            <WorkflowPage.Card title="Total">
                <ReceiptView
                    receipt={{
                        items: undefined,
                        modifiers: {
                            subtotal: {
                                amount: gift.total ?? 0,
                                onUpdate: (newAmount) =>
                                    onUpdateGift(gift.clone({ total: newAmount })),
                            },
                            discount: owner.allowDiscount ? {} : undefined,
                            salesTax: owner.allowSalesTax
                                ? {
                                      amount: gift.taxAmount ?? null,
                                      parenthetical: !!gift.tax ? `${gift.tax}%` : null,
                                  }
                                : undefined,
                            surcharge: owner.allowSurcharge
                                ? {
                                      amount: gift.allowSurcharge ? gift.surchargeAmount : null,
                                      onAdd: () =>
                                          onUpdateGift(gift.clone({ allow_surcharge: true })),
                                      onRemove: () =>
                                          onUpdateGift(gift.clone({ allow_surcharge: false })),
                                  }
                                : undefined,
                        },
                        total: gift.chargeAmount,
                    }}
                    transaction={gift}
                    onUpdateTransaction={onUpdateGift}
                    owner={owner}
                />
            </WorkflowPage.Card>

            <WorkflowPage.NotificationsSection
                transaction={gift}
                onUpdateTransaction={onUpdateGift}
                sendViaDescription="Choose how you'd like your client to be sent this appointment"
                reminderDescription="If a gift certificate remains unpaid, choose how often you’d like your client to be politely reminded until payment is received."
            />

            <WorkflowPage.Card title="More options" defaultIsCollapsed>
                <WorkflowPage.IconSection
                    title="Memo"
                    icon="note.large"
                    secondaryText={`${(gift.memo ?? '').length} / 1500`}
                >
                    <TextArea
                        size="medium"
                        isAutoResizing
                        placeholder="Add a memo"
                        className="overlow-y-hidden"
                        value={gift.memo ?? ''}
                        onChange={(event) => onUpdateGift(gift.clone({ memo: event.target.value }))}
                        maxLength={1500}
                        onClear={() => onUpdateGift(gift.clone({ memo: '' }))}
                    />
                </WorkflowPage.IconSection>

                {owner.allowRecord && (
                    <WorkflowPage.IconSection title="Attach a form" icon="field.large">
                        <TypeSearchableSelect
                            owner={owner}
                            selectedForm={gift.form}
                            onSelectForm={(form) => onUpdateGift(gift.clone({ form }))}
                        />
                    </WorkflowPage.IconSection>
                )}

                {owner.allowRecord && (
                    <WorkflowPage.IconSection title="Attach a contract" icon="pen.large">
                        <ContractSearchableSelect
                            selectedContract={gift.contract}
                            onSelectContract={(contract) => onUpdateGift(gift.clone({ contract }))}
                        />
                    </WorkflowPage.IconSection>
                )}
            </WorkflowPage.Card>
        </div>
    )
}
